import React, { useCallback, useMemo, useState } from 'react'
import { ArrowLeftRight, ChevronDown, ChevronLeft, LineChart, Menu } from 'lucide-react'
import { Link } from 'react-router-dom'
import JSBI from 'jsbi'

import { nutrimaticLabels } from '@/nutrimatic'
import { useTranslation } from '@/features/locale/LocaleProvider'
import { useMobileNavigateBack } from '@/mobile/platform/useMobileNavigateBack'
import { useMobileShellMenuOptional } from '@/mobile/platform/MobileShellMenuContext'
import CurrencyLogo from '../../../../components/CurrencyLogo'
import { cn } from '@/lib/utils'
import { Button } from '@/ui/button'
import { WalletActionButton } from '@/ui/wallet-action-button'
import { WalletActionWaitPanel } from '@/ui/wallet-action-wait-panel'
import { Field } from '../../../../domain/trading'
import { useSwapPoolDetailLink } from '../../../../features/swap/useSwapPoolDetailLink'
import type { SwapControllerResult } from '../../../../features/swap/useSwapController'
import { ApprovalState } from '../../../../hooks/useApproveCallback'
import { useAppWeb3 } from '../../../../hooks'
import { tryParseAmount } from '../../../../state/swap/hooks'
import { useCurrencyBalance } from '../../../../state/wallet/hooks'
import { Currency } from '../../../../sdk'
import {
  formatAmountInputDisplay,
  isValidAmountInputRaw,
  parseAmountInput,
} from '../../../../utils/amountInputFormat'
import { formatSwapAmountDisplay } from '../../../../utils/swapAmountDisplay'
import {
  NUTRIMATIC_BALANCE_FRACTIONS,
  NutrimaticBalancePresetStrip,
} from '../../NutrimaticBalancePresetStrip'
import type { NutrimaticMobileSimpleFlow } from './useNutrimaticMobileSimpleFlow'
import { applyAmountKeypadKey, MobileAmountKeypad } from './MobileAmountKeypad'
import { MobileFluidAmount } from './MobileFluidAmount'
import { MobileSwapQuotePreview, MobileSwapRateHero } from './MobileSwapQuoteBlocks'
import { NutrimaticMobileNotice } from './NutrimaticMobileNotice'
import { useSwapMobileNotice } from './useSwapMobileNotice'
import type { OnChainPairData } from '../../../../features/market/useOnChainPairData'

type ActiveAmountField = Field.INPUT | Field.OUTPUT

function MobileSwapScreenHeader({
  title,
  onBack,
  backLabel,
  poolDetailHref,
  rateHero,
  showBrand = true,
  backDisabled,
  hideBack = false,
  hideTrailing = false,
  className,
}: {
  title: string
  onBack?: () => void
  backLabel?: string
  poolDetailHref?: string | null
  rateHero?: React.ReactNode
  showBrand?: boolean
  backDisabled?: boolean
  hideBack?: boolean
  hideTrailing?: boolean
  className?: string
}) {
  const navigateBack = useMobileNavigateBack()
  const shellMenu = useMobileShellMenuOptional()
  const t = useTranslation()
  const handleBack = onBack ?? navigateBack
  const showTrailing = !hideTrailing && Boolean(poolDetailHref || shellMenu)

  return (
    <header className={cn('nm-mobile-simple__hero-header', className)}>
      <div className="nm-mobile-simple__hero-toolbar">
        <div className="nm-mobile-simple__hero-toolbar-start">
          {hideBack ? (
            <div className="nm-mobile-simple__hero-toolbar-spacer" aria-hidden />
          ) : (
            <button
              type="button"
              className="nm-mobile-simple__icon-btn"
              onClick={handleBack}
              disabled={backDisabled}
              aria-label={backLabel ?? t.common.back}
            >
              <ChevronLeft className="size-5" aria-hidden />
            </button>
          )}
        </div>

        <div className="nm-mobile-simple__hero-heading">
          {showBrand ? (
            <span className="nm-mobile-simple__hero-brand">{nutrimaticLabels.mobileSimple.productBrand}</span>
          ) : null}
          <h1 className="nm-mobile-simple__hero-title">{title}</h1>
        </div>

        {showTrailing ? (
          <div className="nm-mobile-simple__hero-trailing">
            {poolDetailHref ? (
              <Link
                to={poolDetailHref}
                className="nm-mobile-simple__icon-btn"
                aria-label={nutrimaticLabels.mobileSimple.viewPool}
              >
                <LineChart className="size-5" aria-hidden />
              </Link>
            ) : null}
            {shellMenu ? (
              <button
                type="button"
                className="nm-mobile-simple__icon-btn"
                onClick={shellMenu.openMenu}
                aria-label={t.common.openMenu}
              >
                <Menu className="size-5" aria-hidden />
              </button>
            ) : null}
          </div>
        ) : (
          <div className="nm-mobile-simple__hero-trailing" aria-hidden />
        )}
      </div>

      {rateHero ? <div className="nm-mobile-simple__hero-rate">{rateHero}</div> : null}
    </header>
  )
}

export { MobileSwapScreenHeader }

function MobileSimpleCurrencyLeg({
  label,
  value,
  currency,
  onOpenTokenPicker,
  onFocusAmount,
  onAmountChange,
  active,
  disabled,
  position,
  useNativeInput = false,
}: {
  label: string
  value: string
  currency: SwapControllerResult['currencies'][Field.INPUT]
  onOpenTokenPicker: () => void
  onFocusAmount: () => void
  onAmountChange?: (next: string) => void
  active?: boolean
  disabled?: boolean
  position: 'from' | 'to'
  useNativeInput?: boolean
}) {
  const { account } = useAppWeb3()
  const balance = useCurrencyBalance(account ?? undefined, currency ?? undefined)
  const balanceLabel =
    currency && balance
      ? nutrimaticLabels.mobileSimple.balanceAmount(balance.toSignificant(4), currency.symbol ?? '')
      : null

  return (
    <section
      className={cn(
        'nm-mobile-simple__leg',
        position === 'from' ? 'nm-mobile-simple__leg--from' : 'nm-mobile-simple__leg--to',
        disabled && 'pointer-events-none opacity-60',
      )}
      onClick={disabled ? undefined : onFocusAmount}
      role="presentation"
    >
      <div className="nm-mobile-simple__leg-label">{label}</div>

      <div className="nm-mobile-simple__leg-main">
        <button
          type="button"
          className="nm-mobile-simple__token-chip"
          onClick={(event) => {
            event.stopPropagation()
            onOpenTokenPicker()
          }}
        >
          {currency ? <CurrencyLogo currency={currency} size="28px" /> : null}
          <span className="nm-mobile-simple__token-chip-symbol">{currency?.symbol ?? 'Elegir'}</span>
          <span className="nm-mobile-simple__token-chip-chevron" aria-hidden>
            <ChevronDown strokeWidth={2.5} />
          </span>
        </button>

        {useNativeInput ? (
          <input
            type="text"
            inputMode="decimal"
            className={cn(
              'nm-mobile-simple__amount-input',
              active && 'nm-mobile-simple__amount-input--active',
              !value && 'nm-mobile-simple__amount-input--empty',
            )}
            value={formatSwapAmountDisplay(value)}
            placeholder="0"
            onFocus={(event) => {
              event.stopPropagation()
              onFocusAmount()
            }}
            onChange={(event) => {
              event.stopPropagation()
              const nextRaw = parseAmountInput(event.target.value, value)
              if (nextRaw !== '' && !isValidAmountInputRaw(nextRaw)) return
              onAmountChange?.(nextRaw)
            }}
            onClick={(event) => event.stopPropagation()}
            aria-label={`Editar monto ${label}`}
          />
        ) : (
          <MobileFluidAmount
            value={value}
            active={active}
            empty={!value}
            onFocus={() => {
              onFocusAmount()
            }}
            aria-label={`Editar monto ${label}`}
          />
        )}
      </div>

      <div className="nm-mobile-simple__leg-balance-row">
        <span className="nm-mobile-simple__leg-balance-label">{nutrimaticLabels.mobileSimple.balance}</span>
        <span className="nm-mobile-simple__leg-balance-value">
          {account && balanceLabel ? balanceLabel : '—'}
        </span>
      </div>
    </section>
  )
}

export function SwapNutrimaticMobileConvertView({
  controller,
  flow,
  pairData,
  variant = 'mobile',
}: {
  controller: SwapControllerResult
  flow: NutrimaticMobileSimpleFlow
  pairData: OnChainPairData
  variant?: 'mobile' | 'desktop-tablet'
}) {
  const isDesktopTablet = variant === 'desktop-tablet'
  const t = useTranslation()
  const {
    account,
    approval,
    approveActionPending,
    approveActionPhase,
    approveButtonDisabled,
    approveButtonLabel,
    approveCallback,
    canCancelApproveAction,
    cancelApproveAction,
    currencies,
    formattedAmounts,
    handleInputBalanceFraction,
    handleClearAmounts,
    handleSafeMaxInput,
    handleSwapAction,
    handleSwitchTokens,
    handleTypeInput,
    handleTypeOutput,
    isValid,
    presetSpendableInput,
    openConnectModal,
    showApproveFlow,
    swapActionError,
    disabledFractions,
    safeMaxActive,
    safeMaxDisabled,
    showSafeMaxPreset,
    userHasSpecifiedInputOutput,
    activeBalanceFraction,
  } = controller

  const hasPair = Boolean(currencies[Field.INPUT] && currencies[Field.OUTPUT])
  const presetSpendable = presetSpendableInput
  const showBalancePresets = Boolean(currencies[Field.INPUT])
  const balancePresetsDisabled = Boolean(
    !account || !presetSpendable || !presetSpendable.greaterThan(JSBI.BigInt(0)),
  )

  const inApproveFlow = showApproveFlow && approval !== ApprovalState.APPROVED

  const primaryLabel = !account
    ? nutrimaticLabels.mobileSimple.connectWallet
    : inApproveFlow
      ? approveButtonLabel
      : nutrimaticLabels.mobileSimple.reviewSwap

  const primaryDisabled = !account
    ? false
    : inApproveFlow
      ? approveButtonDisabled
      : !isValid || !userHasSpecifiedInputOutput

  const primaryLoading = Boolean(account && inApproveFlow && approveActionPending)
  const showApproveWaitPanel = Boolean(account && inApproveFlow && approveActionPhase !== 'idle')

  const onPrimary = () => {
    if (!account) {
      openConnectModal()
      return
    }
    if (inApproveFlow) {
      approveCallback()
      return
    }
    handleSwapAction()
  }

  const [activeField, setActiveField] = useState<ActiveAmountField>(Field.INPUT)
  const notice = useSwapMobileNotice(controller)
  const { href: poolDetailHref } = useSwapPoolDetailLink(
    currencies[Field.INPUT],
    currencies[Field.OUTPUT],
  )
  const activeCurrencyDecimals =
    activeField === Field.INPUT ? currencies[Field.INPUT]?.decimals : currencies[Field.OUTPUT]?.decimals

  const applyKeypadToField = useCallback(
    (key: string) => {
      const current =
        activeField === Field.INPUT
          ? formattedAmounts[Field.INPUT]
          : formattedAmounts[Field.OUTPUT]
      const next = applyAmountKeypadKey(current, key, { maxDecimals: activeCurrencyDecimals })
      if (activeField === Field.INPUT) {
        handleTypeInput(next)
      } else {
        handleTypeOutput(next)
      }
    },
    [activeCurrencyDecimals, activeField, formattedAmounts, handleTypeInput, handleTypeOutput],
  )

  return (
    <div className="nm-mobile-simple__convert-stage flex min-h-0 flex-1 flex-col overflow-hidden">
      <MobileSwapScreenHeader
        title={nutrimaticLabels.mobileSimple.swapTitle}
        poolDetailHref={isDesktopTablet ? null : poolDetailHref}
        rateHero={isDesktopTablet ? undefined : <MobileSwapRateHero controller={controller} pairData={pairData} />}
        hideBack={isDesktopTablet}
        hideTrailing={isDesktopTablet}
      />

      <div className="nm-mobile-simple__body nm-mobile-simple__body--trade" data-mobile-scroll-container>
        {notice ? <NutrimaticMobileNotice {...notice} /> : null}

        <div className="nm-mobile-simple__trade-stack">
          <div className="nm-mobile-simple__legs-pair">
            <MobileSimpleCurrencyLeg
              label={nutrimaticLabels.mobileSimple.from}
              value={formattedAmounts[Field.INPUT]}
              currency={currencies[Field.INPUT]}
              onOpenTokenPicker={() => flow.openTokenPicker(Field.INPUT)}
              onFocusAmount={() => setActiveField(Field.INPUT)}
              onAmountChange={handleTypeInput}
              active={activeField === Field.INPUT}
              position="from"
              useNativeInput={isDesktopTablet}
            />

            <MobileSimpleCurrencyLeg
              label={nutrimaticLabels.mobileSimple.to}
              value={formattedAmounts[Field.OUTPUT]}
              currency={currencies[Field.OUTPUT]}
              onOpenTokenPicker={() => flow.openTokenPicker(Field.OUTPUT)}
              onFocusAmount={() => setActiveField(Field.OUTPUT)}
              onAmountChange={handleTypeOutput}
              active={activeField === Field.OUTPUT}
              position="to"
              useNativeInput={isDesktopTablet}
            />

            <div className="nm-mobile-simple__swap-bridge">
              <button
                type="button"
                onClick={handleSwitchTokens}
                disabled={!hasPair}
                aria-label={t.common.invertSwapDirection}
              >
                <ArrowLeftRight className="size-4" aria-hidden />
              </button>
            </div>
          </div>

          <MobileSwapQuotePreview controller={controller} />
        </div>
      </div>

      <div className={cn('nm-mobile-simple__input-dock', isDesktopTablet && 'nm-mobile-simple__input-dock--desktop')}>
        <div className="nm-mobile-simple__footer">
          {showApproveWaitPanel ? (
            <WalletActionWaitPanel
              phase={approveActionPhase === 'awaiting-confirmation' ? 'awaiting-confirmation' : 'awaiting-wallet'}
              onCancel={canCancelApproveAction ? cancelApproveAction : undefined}
            />
          ) : (
            <WalletActionButton
              type="button"
              className="nm-mobile-simple__cta"
              disabled={primaryDisabled}
              loading={primaryLoading}
              onClick={onPrimary}
            >
              {account && swapActionError && !showApproveFlow ? swapActionError : primaryLabel}
            </WalletActionButton>
          )}
        </div>

        {showBalancePresets ? (
          <NutrimaticBalancePresetStrip
            variant="mobile"
            fractions={NUTRIMATIC_BALANCE_FRACTIONS}
            activeFraction={activeBalanceFraction}
            onSelect={(fraction) => {
              setActiveField(Field.INPUT)
              handleInputBalanceFraction(fraction)
            }}
            disabled={balancePresetsDisabled}
            disabledFractions={disabledFractions}
            onClear={() => {
              setActiveField(Field.INPUT)
              handleClearAmounts()
            }}
            clearDisabled={!userHasSpecifiedInputOutput}
            showSafeMax={showSafeMaxPreset}
            onSafeMax={() => {
              setActiveField(Field.INPUT)
              handleSafeMaxInput()
            }}
            safeMaxDisabled={safeMaxDisabled}
            safeMaxActive={safeMaxActive}
          />
        ) : null}

        {!isDesktopTablet ? <MobileAmountKeypad onKeyPress={applyKeypadToField} /> : null}
      </div>
    </div>
  )
}

function MobileSimpleHeader({
  title,
  onBack,
  backLabel,
}: {
  title: string
  onBack?: () => void
  backLabel?: string
}) {
  return <MobileSwapScreenHeader title={title} onBack={onBack} backLabel={backLabel} />
}
